import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './AccessibilityButton.css';

const AccessibilityButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100); // percentage
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [colorBlindMode, setColorBlindMode] = useState(false);
  const { currentLanguage } = useLanguage();

  const translations = {
    tr: {
      accessibility: 'Erişilebilirlik',
      increaseFont: 'Yazı Boyutunu Artır',
      decreaseFont: 'Yazı Boyutunu Azalt',
      highContrast: 'Yüksek Kontrast',
      reducedMotion: 'Azaltılmış Hareket',
      darkMode: 'Karanlık Mod',
      colorBlindMode: 'Renk Körü Modu',
      reset: 'Sıfırla',
      close: 'Kapat'
    },
    de: {
      accessibility: 'Barrierefreiheit',
      increaseFont: 'Schriftgröße erhöhen',
      decreaseFont: 'Schriftgröße verringern',
      highContrast: 'Hoher Kontrast',
      reducedMotion: 'Reduzierte Bewegung',
      darkMode: 'Dunkelmodus',
      colorBlindMode: 'Farbenblindmodus',
      reset: 'Zurücksetzen',
      close: 'Schließen'
    },
    en: {
      accessibility: 'Accessibility',
      increaseFont: 'Increase Font Size',
      decreaseFont: 'Decrease Font Size',
      highContrast: 'High Contrast',
      reducedMotion: 'Reduced Motion',
      darkMode: 'Dark Mode',
      colorBlindMode: 'Color Blind Mode',
      reset: 'Reset',
      close: 'Close'
    }
  };

  const t = translations[currentLanguage] || translations.tr;

  useEffect(() => {
    // Apply accessibility settings
    document.documentElement.style.fontSize = `${fontSize}%`;
    
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    if (reducedMotion) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
    
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    if (colorBlindMode) {
      document.body.classList.add('color-blind-mode');
    } else {
      document.body.classList.remove('color-blind-mode');
    }
  }, [fontSize, highContrast, reducedMotion, darkMode, colorBlindMode]);

  const increaseFontSize = (e) => {
    e.stopPropagation();
    setFontSize(prev => Math.min(prev + 10, 200));
  };

  const decreaseFontSize = (e) => {
    e.stopPropagation();
    setFontSize(prev => Math.max(prev - 10, 70));
  };

  const resetSettings = (e) => {
    e.stopPropagation();
    setFontSize(100);
    setHighContrast(false);
    setReducedMotion(false);
    setDarkMode(false);
    setColorBlindMode(false);
  };

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const handleCheckboxChange = (setter) => (e) => {
    e.stopPropagation();
    setter(e.target.checked);
  };

  return (
    <div className="accessibility-container">
      {/* Main accessibility button */}
      <button 
        className="accessibility-toggle"
        onClick={togglePanel}
        aria-label={t.accessibility}
        aria-expanded={isOpen}
      >
        <img src="/Accessibility.png" alt="Accessibility" width="24" height="24" />
      </button>

      {/* Accessibility panel */}
      {isOpen && (
        <div className="accessibility-panel" onClick={(e) => e.stopPropagation()}>
          <div className="accessibility-header">
            <h3>{t.accessibility}</h3>
            <button 
              className="close-button"
              onClick={togglePanel}
              aria-label={t.close}
            >
              ×
            </button>
          </div>
          
          <div className="accessibility-options">
            <div className="option-group">
              <label>{t.increaseFont}</label>
              <button 
                className="accessibility-btn"
                onClick={increaseFontSize}
                aria-label={t.increaseFont}
              >
                A+
              </button>
            </div>
            
            <div className="option-group">
              <label>{t.decreaseFont}</label>
              <button 
                className="accessibility-btn"
                onClick={decreaseFontSize}
                aria-label={t.decreaseFont}
              >
                A-
              </button>
            </div>
            
            <div className="option-group">
              <label>
                <input
                  type="checkbox"
                  checked={highContrast}
                  onChange={handleCheckboxChange(setHighContrast)}
                />
                {t.highContrast}
              </label>
            </div>
            
            <div className="option-group">
              <label>
                <input
                  type="checkbox"
                  checked={reducedMotion}
                  onChange={handleCheckboxChange(setReducedMotion)}
                />
                {t.reducedMotion}
              </label>
            </div>
            
            <div className="option-group">
              <label>
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={handleCheckboxChange(setDarkMode)}
                />
                {t.darkMode}
              </label>
            </div>
            
            <div className="option-group">
              <label>
                <input
                  type="checkbox"
                  checked={colorBlindMode}
                  onChange={handleCheckboxChange(setColorBlindMode)}
                />
                {t.colorBlindMode}
              </label>
            </div>
            
            <button 
              className="reset-button"
              onClick={resetSettings}
            >
              {t.reset}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityButton; 